import StudioBrand from './StudioBrand';
import { useMemo, useState } from 'react';
import { Archive, ArchiveRestore, Building2, CheckCircle2, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { createTrustProfile, deleteTrustProfile, importLegacyTrustProfile, mergeTrustDirectory, setTrustActiveState, setTrustJourneyLimit, setTrustPrintEntitlements, resetNHNNSetup } from '../lib/trustRegistryApi';
import { loadCloudTrustConfig } from '../lib/trustConfigApi';
import { CONFIGURABLE_JOURNEY_LIBRARY as BASE_CONFIGURABLE_JOURNEY_LIBRARY } from '../../../src/data/configurableJourneyLibrary.js';
import { NHNN_STUDIO_STARTERS } from '../lib/nhnnStudioStarters.js';
const CONFIGURABLE_JOURNEY_LIBRARY = [...BASE_CONFIGURABLE_JOURNEY_LIBRARY, ...NHNN_STUDIO_STARTERS];

const blankDraft = { id: '', name: '', shortName: '', odsCode: '', websiteUrl: '', journeyLimit: '' };

export default function TrustRegistryStudio({ workspaceSwitcher = null, studioAccess = null, studioTrusts = [], onTrustsChanged = null }) {
  const [draft, setDraft] = useState(blankDraft);
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);
  const [importingId, setImportingId] = useState(null);
  const [changingId, setChangingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [licenceDrafts, setLicenceDrafts] = useState({});
  const [licenceSavingId, setLicenceSavingId] = useState(null);
  const [resettingNHNN, setResettingNHNN] = useState(false);
  const resetNHNNPrePilot = async () => {
    setResettingNHNN(true);
    setNotice('');
    try {
      // Never reset stale client-side data. The server also checks status and ETag.
      const current = await loadCloudTrustConfig('nhnn_uclh');
      if (current?.configuration?.status !== 'draft' || current?.publication?.live) {
        throw new Error('NHNN must be an unpublished draft before its setup can be reset.');
      }
      const confirmation = window.prompt(
        'Reset NHNN pre-pilot setup? This clears Know Your Hospital links, Trust role selections, local uniform definitions and care-setting choices. It preserves registered users, both pilot Journey sources, configured Journeys and clinical review records. The existing setup is backed up for recovery. Type nhnn_uclh to confirm:'
      );
      if (confirmation !== 'nhnn_uclh') {
        setNotice('NHNN setup reset cancelled. No data was changed.');
        return;
      }
      const result = await resetNHNNSetup(current.configuration.updatedAt || null);
      setNotice(`NHNN local setup reset successfully. ${result.preservedJourneyIds.length} previously configured Journeys preserved. Trust Admins can start fresh with roles, uniforms and hospital information.`);
    } catch (error) {
      setNotice(error.message || 'The NHNN setup reset could not be completed.');
    } finally {
      setResettingNHNN(false);
    }
  };
  const [printDrafts, setPrintDrafts] = useState({});
  const [printSavingId, setPrintSavingId] = useState(null);
  const standardPrintJourneys = CONFIGURABLE_JOURNEY_LIBRARY.filter(item => item.category !== 'easy_read');
  const easyReadJourneys = CONFIGURABLE_JOURNEY_LIBRARY.filter(item => item.category === 'easy_read');
  const printDraftFor = trust => printDrafts[trust.id] || {
    licensedJourneyIds: trust.licensedJourneyIds || [],
    easyReadPdfJourneyIds: trust.easyReadPdfJourneyIds || [],
  };
  const togglePrintJourney = (trust, field, id) => {
    const draft = printDraftFor(trust);
    const current = draft[field];
    setPrintDrafts(old => ({
      ...old,
      [trust.id]: {
        ...draft,
        [field]: current.includes(id) ? current.filter(value => value !== id) : [...current, id],
      },
    }));
  };
  const savePrintRights = async trust => {
    setPrintSavingId(trust.id);
    setNotice('');
    try {
      const draft = printDraftFor(trust);
      const response = await setTrustPrintEntitlements(trust.id, draft.licensedJourneyIds, draft.easyReadPdfJourneyIds);
      onTrustsChanged?.(mergeTrustDirectory(studioTrusts, response.trust));
      setPrintDrafts(old => ({ ...old, [trust.id]: {
        licensedJourneyIds: response.trust.licensedJourneyIds,
        easyReadPdfJourneyIds: response.trust.easyReadPdfJourneyIds,
      } }));
      setNotice('Contract print permissions saved. Unlisted Journeys remain unavailable for PDF export.');
    } catch (error) {
      setNotice(error.message || 'Could not update print permissions.');
    } finally {
      setPrintSavingId(null);
    }
  };
  const platformAdmin = studioAccess?.roles?.includes('platform_admin');

  const canSave = useMemo(() =>
    platformAdmin
    && /^[a-z0-9]+(?:_[a-z0-9]+)*$/.test(draft.id)
    && draft.name.trim().length >= 3
    && draft.shortName.trim().length >= 2
    && (!draft.websiteUrl || draft.websiteUrl.startsWith('https://'))
    && (!draft.journeyLimit || (Number.isInteger(Number(draft.journeyLimit)) && Number(draft.journeyLimit) >= 1 && Number(draft.journeyLimit) <= 100)),
  [draft, platformAdmin]);

  const change = (field, value) => {
    const nextValue = field === 'id'
      ? value.toLowerCase().replace(/[^a-z0-9_]/g, '')
      : field === 'odsCode' ? value.toUpperCase().replace(/[^A-Z0-9]/g, '') : value;
    setDraft((current) => ({ ...current, [field]: nextValue }));
  };

  const create = async () => {
    if (!canSave) return;
    setSaving(true);
    setNotice('');
    try {
      const response = await createTrustProfile(draft);
      const nextTrusts = mergeTrustDirectory(studioTrusts, response.trust);
      onTrustsChanged?.(nextTrusts);
      setDraft(blankDraft);
      setNotice(`${response.trust.shortName} was created as a private draft. You can now assign its Trust administrator in People and access.`);
    } catch (error) {
      setNotice(error.message);
    } finally {
      setSaving(false);
    }
  };

  const importLegacy = async (trust) => {
    setImportingId(trust.id);
    setNotice('');
    try {
      const active = trust.active !== false;
      const response = await importLegacyTrustProfile({
        id: trust.id,
        name: trust.name,
        shortName: trust.shortName,
        active,
        aliases: trust.id === 'nhnn_uclh' ? ['uclh_nhnn'] : [],
        legacyProfile: trust.legacyProfile,
      });
      const nextTrusts = mergeTrustDirectory(studioTrusts, response.trust);
      onTrustsChanged?.(nextTrusts);
      setNotice(active
        ? `${response.trust.shortName} is now managed by Studio. Its existing configuration has been preserved as a private draft and nothing has been published.`
        : `${response.trust.shortName} is now registered in Studio as deactivated. Its legacy configuration has been preserved.`);
    } catch (error) {
      setNotice(error.message);
    } finally {
      setImportingId(null);
    }
  };

  const saveJourneyLicence = async (trust) => {
    const raw = Object.prototype.hasOwnProperty.call(licenceDrafts, trust.id) ? licenceDrafts[trust.id] : (trust.journeyLimit ?? '');
    if (raw !== '' && (!Number.isInteger(Number(raw)) || Number(raw) < 1 || Number(raw) > 100)) {
      setNotice('Journey licence limit must be a whole number from 1 to 100, or left blank for unlimited.');
      return;
    }
    setLicenceSavingId(trust.id);
    setNotice('');
    try {
      const response = await setTrustJourneyLimit(trust.id, raw);
      const nextTrusts = mergeTrustDirectory(studioTrusts, response.trust);
      onTrustsChanged?.(nextTrusts);
      setLicenceDrafts((current) => ({ ...current, [trust.id]: response.trust.journeyLimit ?? '' }));
      setNotice(response.trust.journeyLimit
        ? `${response.trust.shortName} can now configure up to ${response.trust.journeyLimit} Journey${response.trust.journeyLimit === 1 ? '' : 's'}.`
        : `${response.trust.shortName} now has no Journey configuration limit.`);
    } catch (error) {
      setNotice(error.message);
    } finally {
      setLicenceSavingId(null);
    }
  };

  const changeLifecycle = async (trust, active) => {
    if (!active) {
      const confirmed = window.confirm(`Deactivate ${trust.shortName || trust.name}?\n\nTrust administrators will no longer be able to access this Trust in Studio. Its configuration will be preserved. This action does not delete the Trust.`);
      if (!confirmed) return;
    }
    setChangingId(trust.id);
    setNotice('');
    try {
      const response = await setTrustActiveState(trust.id, active);
      const nextTrusts = mergeTrustDirectory(studioTrusts, response.trust);
      onTrustsChanged?.(nextTrusts);
      setNotice(active
        ? `${response.trust.shortName} has been reactivated in Studio. Its preserved configuration remains a private draft and has not been published.`
        : `${response.trust.shortName} has been deactivated. Its configuration has been preserved.`);
    } catch (error) {
      setNotice(error.message);
    } finally {
      setChangingId(null);
    }
  };

  const deletePermanently = async (trust) => {
    const entered = window.prompt(
      `Permanently delete ${trust.shortName || trust.name}?\n\nThis removes the Trust profile, configuration, publication history and audit history. It cannot be undone.\n\nType the exact Trust ID to continue:\n${trust.id}`
    );
    if (entered === null) return;
    if (entered.trim() !== trust.id) {
      setNotice('Permanent deletion cancelled because the Trust ID did not match.');
      return;
    }

    setDeletingId(trust.id);
    setNotice('');
    try {
      await deleteTrustProfile(trust.id, entered.trim());
      onTrustsChanged?.(studioTrusts.filter((item) => item.id !== trust.id));
      setNotice(`${trust.shortName || trust.name} has been permanently deleted from Studio.`);
    } catch (error) {
      setNotice(error.message);
    } finally {
      setDeletingId(null);
    }
  };

  if (!platformAdmin) return <main className="access-state"><ShieldCheck size={34}/><h1>Platform administrator access required</h1></main>;

  return <div className="app-shell">
    <header className="topbar"><div className="brand"><span className="brand-mark"><Building2/></span><div><strong><StudioBrand /></strong><small>Trust directory</small></div></div></header>
    {notice && <div className="studio-notice" role="status"><span>{notice}</span><button onClick={() => setNotice('')} aria-label="Dismiss notification">×</button></div>}
    <div className="workspace">
      <aside className="catalogue">
        {workspaceSwitcher}
        <div className="catalogue-intro"><p className="eyebrow">Platform administration</p><h1>Trusts</h1><p>This directory and its creation controls are visible only to platform administrators.</p></div>
        <div className="journey-studio-stat"><Building2 size={20}/><div><strong>{studioTrusts.filter((trust) => trust.id !== 'generic_nhs').length} Trust profiles</strong><span>Drafts stay private until reviewed and published</span></div></div>
      </aside>
      <main className="review-workspace">
        <section className="trust-config-panel">
          <div className="trust-panel-heading"><div><p className="eyebrow">Private draft</p><h2><Plus size={20}/> Create a Trust profile</h2><p>Use an immutable internal ID. Creating a profile does not publish it to the patient-facing app.</p></div></div>
          <div className="config-fields">
            <label>Official Trust name<input value={draft.name} onChange={(event) => change('name', event.target.value)} placeholder="Example NHS Foundation Trust"/></label>
            <label>Short display name<input value={draft.shortName} onChange={(event) => change('shortName', event.target.value)} placeholder="Example NHS"/></label>
            <label>Trust ID<input value={draft.id} onChange={(event) => change('id', event.target.value)} placeholder="example_nhs"/><small>Lowercase letters, numbers and underscores. This cannot be changed later.</small></label>
            <label>ODS code (optional)<input value={draft.odsCode} onChange={(event) => change('odsCode', event.target.value)} placeholder="ABC"/></label>
            <label>Main website (optional)<input type="url" value={draft.websiteUrl} onChange={(event) => change('websiteUrl', event.target.value)} placeholder="https://www.example.nhs.uk"/></label>
            <label>Journey licence limit (optional)<input type="number" min="1" max="100" value={draft.journeyLimit} onChange={(event) => change('journeyLimit', event.target.value)} placeholder="Leave blank for unlimited"/><small>Controls how many Journeys this Trust can add to its configuration. Existing configured Journeys cannot be removed by lowering the limit.</small></label>
          </div>
          <div className="trust-actions"><button className="button primary" disabled={!canSave || saving} onClick={create}><Plus size={16}/>{saving ? 'Creating…' : 'Create private draft'}</button></div>
        </section>
        <section className="trust-config-panel">
          <div className="trust-panel-heading"><div><h2>Trust directory</h2><p>Only you can see the complete directory. Trust administrators receive only their assigned active Trust.</p></div></div>
          <div className="config-section"><div className="check-grid">{studioTrusts.filter((trust) => trust.id !== 'generic_nhs').map((trust) => <div key={trust.id} className={`review-sync-card ${trust.active === false ? '' : 'connected'}`}><CheckCircle2 size={17}/><div><strong>{trust.shortName || trust.name}</strong><small>{trust.name} · {trust.odsCode || 'ODS not recorded'} · {trust.active === false ? 'Deactivated' : (trust.lifecycleStatus || 'existing')}</small>{!trust.registered && <button className="button secondary" type="button" disabled={importingId === trust.id} onClick={() => importLegacy(trust)}><ArchiveRestore size={15}/>{importingId === trust.id ? 'Adding…' : (trust.active === false ? 'Add to Studio as deactivated' : 'Add to Studio management')}</button>}{trust.registered && <><div className="config-fields" style={{marginTop:10}}><label className="trust-journey-licence-field">Journey licence limit<input type="number" min="1" max="100" value={Object.prototype.hasOwnProperty.call(licenceDrafts, trust.id) ? licenceDrafts[trust.id] : (trust.journeyLimit ?? '')} onChange={(event) => setLicenceDrafts((current) => ({ ...current, [trust.id]: event.target.value }))} placeholder="Unlimited"/><small>Leave blank for unlimited. This sets the maximum allowed; add or remove configured Journeys in the Journeys workspace.</small><small>{trust.journeyLimit ? `Current licence: up to ${trust.journeyLimit} Journey${trust.journeyLimit === 1 ? '' : 's'}` : 'Current licence: unlimited Journeys'}</small></label>
              <details className="trust-print-entitlements">
                <summary><strong>Contract PDF permissions</strong> — Standard / Large Text: {printDraftFor(trust).licensedJourneyIds.length} · Easy Read: {printDraftFor(trust).easyReadPdfJourneyIds.length}</summary>
                <p>Only explicitly ticked Journeys may be exported. Standard and Large Text also require a configured, currently clinically reviewed Journey. Easy Read is a separate, optional entitlement.</p>
                <div className="trust-print-permission-list" style={{maxHeight:220, overflowY:'auto', display:'grid', gap:6}}>
                  <strong>Standard and Large Text PDFs</strong>
                  {standardPrintJourneys.map(item => <label key={item.id}><input type="checkbox" checked={printDraftFor(trust).licensedJourneyIds.includes(item.id)} onChange={() => togglePrintJourney(trust, 'licensedJourneyIds', item.id)}/>{item.title}</label>)}
                </div>
                <div className="trust-print-permission-list" style={{maxHeight:180, overflowY:'auto', display:'grid', gap:6, marginTop:12}}>
                  <strong>Easy Read PDFs (separate agreement)</strong>
                  {easyReadJourneys.map(item => <label key={item.id}><input type="checkbox" checked={printDraftFor(trust).easyReadPdfJourneyIds.includes(item.id)} onChange={() => togglePrintJourney(trust, 'easyReadPdfJourneyIds', item.id)}/>{item.title}</label>)}
                </div>
                <div className="trust-actions"><button className="button primary" type="button" disabled={printSavingId === trust.id} onClick={() => savePrintRights(trust)}>{printSavingId === trust.id ? 'Saving permissions…' : 'Save contract PDF permissions'}</button></div>
              </details>
              {trust.id === 'nhnn_uclh' && <details className="trust-prepilot-reset">
                <summary>NHNN — reset pre-pilot setup</summary>
                <p>Use once before inviting NHNN Trust Admins. Removes existing KYH links, Trust-specific role/uniform selections and care-setting settings. Keeps user access, published-history records and all configured Journeys; private example source links are unaffected. Blocked for a live or non-draft Trust.</p>
                <button type="button" className="button danger-quiet" disabled={resettingNHNN} onClick={resetNHNNPrePilot}>{resettingNHNN ? 'Checking NHNN configuration…' : 'Reset NHNN setup'}</button>
              </details>}
              </div><div className="trust-actions"><button className="button secondary" type="button" disabled={licenceSavingId === trust.id} onClick={() => saveJourneyLicence(trust)}>{licenceSavingId === trust.id ? 'Saving limit…' : 'Save Journey limit'}</button>{trust.active === false ? <><button className="button primary" type="button" disabled={changingId === trust.id || deletingId === trust.id} onClick={() => changeLifecycle(trust, true)}><ArchiveRestore size={15}/>{changingId === trust.id ? 'Reactivating…' : 'Reactivate Trust'}</button><button className="button danger-quiet" type="button" disabled={deletingId === trust.id || changingId === trust.id} onClick={() => deletePermanently(trust)}><Trash2 size={15}/>{deletingId === trust.id ? 'Deleting…' : 'Delete Trust permanently'}</button></> : <button className="button secondary" type="button" disabled={changingId === trust.id} onClick={() => changeLifecycle(trust, false)}><Archive size={15}/>{changingId === trust.id ? 'Deactivating…' : 'Deactivate Trust'}</button>}</div></>}</div></div>)}</div></div>
          <small>Permanent deletion is available only for deactivated Trusts. Patient access must be disabled and any People and access assignments removed first.</small>
        </section>
      </main>
    </div>
  </div>;
}
